import { apiFetch } from './client';

export type DidCoverage = 'us' | 'ca' | 'international';
// Migration 0086 added 'ring_group' to the legal kinds and wired
// engine dispatch + UI for all four non-IVR kinds.
// 2026-07-02 (Sam): ALL DID mutations (routing, assign/release, active)
// are super_admin + reseller_owner only — customer-tier roles are
// read-only on DIDs. `dids.update` from a customer-tier session returns
// 403 forbidden.
export type RoutingKind = 'ivr' | 'extension' | 'ring_group' | 'voicemail' | 'transfer';

export interface DidRow {
  id: string;
  reseller_id: string | null;
  customer_id: string | null;
  /** Owner display fields from the list endpoint (rule 28 — names + human
   *  numbers, never raw UUIDs). NULL when unassigned / hidden by RLS. */
  customer_name?: string | null;
  customer_number?: number | null;
  reseller_name?: string | null;
  reseller_number?: number | null;
  ref_number: string | null;
  e164: string;
  coverage: DidCoverage;
  premium: boolean;
  carrier_id: string | null;
  fs_gateway_name: string | null;
  routing_kind: RoutingKind;
  routing_target_id: string | null;
  /** SIP URI / E.164 — populated only when routing_kind='transfer'.
   *  Migration 0086 CHECK constraint enforces the column-vs-kind
   *  shape so a stray PATCH can't put junk in the wrong field. */
  transfer_uri: string | null;
  /** Migration 0087 — scheduled routing. When set, the engine
   *  evaluates the frame at call time and uses the off_frame_* tuple
   *  on not_in_frame. NULL = unscheduled (primary tuple always wins). */
  time_frame_id: string | null;
  off_frame_routing_kind: RoutingKind | null;
  off_frame_routing_target_id: string | null;
  off_frame_transfer_uri: string | null;
  active: boolean;
  assigned_at: string | null;
  released_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateDidBody {
  e164: string;
  coverage?: DidCoverage;
  premium?: boolean;
  carrier_id?: string;
  fs_gateway_name?: string;
  /** Optional: drop the new DID directly into a reseller's pool at creation
   *  time. Super_admin-only — backend rejects from other roles. */
  reseller_id?: string;
  /** Optional: pre-assign to a customer at creation. Requires reseller_id
   *  to also be set. Super_admin-only. */
  customer_id?: string;
}

export type UpdateDidBody = Partial<
  Pick<
    DidRow,
    | 'reseller_id'
    | 'customer_id'
    | 'routing_kind'
    | 'routing_target_id'
    | 'transfer_uri'
    | 'time_frame_id'
    | 'off_frame_routing_kind'
    | 'off_frame_routing_target_id'
    | 'off_frame_transfer_uri'
    | 'carrier_id'
    | 'fs_gateway_name'
    | 'active'
  >
>;

export const dids = {
  list: (p: {
    search?: string;
    assigned?: 'all' | 'assigned' | 'unassigned';
    limit?: number;
    /** Scope to one customer (e.g. on the scoped /customer/:cn/account
     *  page so super_admin sees only THIS customer's numbers). */
    customerId?: string;
    /** Scope to one reseller. */
    resellerId?: string;
  } = {}) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.assigned) q.set('assigned', p.assigned);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.customerId) q.set('customer_id', p.customerId);
    if (p.resellerId) q.set('reseller_id', p.resellerId);
    return apiFetch<{ rows: DidRow[] }>(`/api/dids?${q}`);
  },
  get: (id: string) => apiFetch<DidRow>(`/api/dids/${id}`),
  create: (body: CreateDidBody) =>
    apiFetch<DidRow>('/api/dids', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, patch: UpdateDidBody) =>
    apiFetch<DidRow>(`/api/dids/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/dids/${id}`, { method: 'DELETE' }),
};
